// ImageEditor — the Image block's settings pane, restyled to the WL-011 framework.
// WL-011 (Fable Hail Mary Wave 2). Deck: concepts/edit-wheel-flow/20 (image tab),
// 20b (advanced), 21 (sources). Spec §2.4. Gordon ruling C2 = Alignment is BASIC.
//
// Feature-completeness (law): mirrors EVERY control in the live editor
// components/SegmentBuilder/CodeBlocks/Image/ImageSettings.jsx — image source
// (Upload/Embed URL/Browse/Recent, + "Take Photo" when takePhotoEnabled — 2026-08-19
// in-place capture, MediaCapture/WebcamPhotoCapture) + drop zone, uploaded-image
// card (name, Display Caption, Copy URL, delete), Alignment, Add Hyperlink (+URL),
// Resizing, Corner Rounding, Border (+width), Drop Shadow, Margin.
//
// BASIC   = choose the image (sources + drop zone + uploaded card) + Alignment.
// ADVANCED= Add Hyperlink to Image, Resizing, Corner Rounding, Border, Drop Shadow, Margin.
//
// EW-012 contracts: pass status="empty" (no image yet), "loading" (uploading),
// "error" (upload/load failed) to the pane. EW-015: the hyperlink URL is
// validated (bad links surfaced inline, never saved silently).
//
// WL-011c: the source area is now FUNCTIONAL when the live-wiring adapter supplies
// the pipeline props (onUpload + uploadStatus/uploadError + renderBrowse/renderRecent):
// the Upload dropzone takes a real file (click or drag-drop), shows a progress
// spinner + inline error, and the Browse/Recent tabs render the shared library/recent
// pickers. When those props are omitted (static design deck) the area degrades to a
// non-functional preview — no behavior change for the deck.

import React, { useRef, useState } from "react";
import styled from "styled-components";
import { CircularProgress } from "@mui/material";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import AdvancedOptions from "../AdvancedOptions";
import {
  TabsRow, SegChoice, Toggle, TextInputField,
  LabeledSlider, ColorField, QuadStepper,
} from "../controls";
import { url, validateField } from "../validation";
import { fontFamily, focusRing } from "../../../../theme/tokens";
import { fieldChrome } from "../EditorField";
import { validateImageFile, IMAGE_ACCEPT } from "./imageUpload";
import {
  IMAGE_CAPTION_OVERLAY_ENABLED,
  MEDIA_RECORDER_ENABLED,
} from "../../../../constants/featureFlags";
import CaptionOverlayControl from "../../Shared/CaptionOverlay/CaptionOverlayControl";
import WebcamPhotoCapture from "../../../MediaCapture/WebcamPhotoCapture";
import { canCapturePhoto } from "../../../MediaCapture";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const PURPLE = fieldChrome.accent;
const BORDER = fieldChrome.border;

const ImageGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.5" cy="9.5" r="1.6" stroke="currentColor" strokeWidth="1.3" />
    <path d="M5 17l4.5-4.5 3 3L16 12l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DropZone = styled.div`
  border: 1.5px dashed ${(p) => (p.$active ? PURPLE : BORDER)};
  border-radius: 10px;
  padding: 24px 12px;
  text-align: center;
  font-family: ${fontFamily};
  font-size: 13px;
  color: ${MUTED};
  background: ${(p) => (p.$active ? "#f6f2ff" : "#fbfbfc")};
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const DZLink = styled.button`
  border: none; background: transparent; padding: 4px;
  color: ${PURPLE}; font-family: ${fontFamily}; font-size: 13px; font-weight: 600;
  cursor: pointer; text-decoration: underline;
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const DZProgress = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  font-family: ${fontFamily}; font-size: 13px; color: ${MUTED};
`;
const UploadError = styled.div`
  margin-top: 8px; padding: 8px 10px; border-radius: 8px;
  background: #fff0f0; color: ${fieldChrome.error};
  font-family: ${fontFamily}; font-size: 12px;
`;
const HiddenFileInput = styled.input` display: none; `;

const UpCard = styled.div`
  display: flex; align-items: center; gap: 10px;
  border: 1px solid ${BORDER}; border-radius: 10px; padding: 10px;
  font-family: ${fontFamily};
`;
const Thumb = styled.div`
  width: 48px; height: 48px; flex: none; border-radius: 8px;
  background: #ede4fe center/cover no-repeat;
  ${(p) => (p.$src ? `background-image:url("${p.$src}");` : "")}
`;
const UpMeta = styled.div` flex: 1; min-width: 0; `;
const UpName = styled.div`
  font-size: 13px; font-weight: 600; color: ${INK};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const UpActions = styled.div` display: flex; gap: 4px; `;
const IconBtn = styled.button`
  min-width: 44px; min-height: 44px; padding: 0 8px;
  border: none; background: transparent; border-radius: 8px;
  color: ${(p) => (p.$danger ? fieldChrome.error : PURPLE)};
  font-family: ${fontFamily}; font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { background: #f6f7f9; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;

const DEFAULT = {
  source: "Upload",
  src: "",
  title: "",
  showImageTitle: false,
  alignment: "left",
  showHyperlink: false,
  hyperlink: "",
  width: 100,
  borderRadius: 0,
  borderColor: "#E4E7EC",
  borderWidth: 0,
  dropShadowColor: "",
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  applyMarginAll: false,
};

const ImageEditor = ({
  value = DEFAULT,
  onChange = () => {},
  segmentName,
  onBack,
  status,
  onRetry,
  errorMessage,
  onCopyUrl,
  onRemove,
  // WL-011c: the real S3-upload / browse / recent pipeline, supplied by the
  // live-wiring adapter (ImageEditorSettings). When these are omitted (e.g. the
  // static design deck) the source area falls back to a non-functional preview.
  onUpload,
  uploadStatus = "idle", // "idle" | "uploading" | "error"
  uploadError,
  renderBrowse,
  renderRecent,
  // T22.136: the live Embed URL pipeline (shared EmbedURL: re-host + save to
  // Recent / the Media Library), supplied by the live-wiring adapter. Omitted
  // in the static deck -> the plain URL field below, byte-identical.
  renderEmbed,
  // DL-048 adoption: optional "Reuse from your Media Library" affordance, always
  // visible under the source tabs (the live-wiring adapter supplies it). Omitted in
  // the static deck -> no reuse affordance, byte-identical.
  renderLibrary,
  gifAllowed = true,
  // Default = the build-time flag; a prop seam so the OFF/ON split is unit-testable
  // without juggling process.env. Live callers never pass it (== the constant).
  captionOverlayEnabled = IMAGE_CAPTION_OVERLAY_ENABLED,
  // "Take Photo" source (MediaCapture/WebcamPhotoCapture): same seam pattern as
  // captionOverlayEnabled above — default = flag AND real feature-detect, so an
  // unsupported browser silently gets Upload-only (never a dead-end tab) while
  // a test can force either state without stubbing getUserMedia globally.
  takePhotoEnabled = MEDIA_RECORDER_ENABLED && canCapturePhoto(),
}) => {
  const v = { ...DEFAULT, ...value };
  const patch = (p) => onChange({ ...v, ...p });
  // Parity (WL-011c): the Image editor's PRIMARY job is to CHOOSE the image, so the
  // source-selection UI (Upload/Embed/Browse/Recent) must ALWAYS be reachable —
  // exactly like the legacy editor, which always renders MediaSelection and shows the
  // uploaded-image card only once a picture exists. We therefore do NOT collapse to
  // the pane's "empty" placeholder when there's no image yet (that would hide the
  // uploader and make the block un-fillable). The host can still pass an explicit
  // status ("loading"/"error"/"empty") — e.g. the static design deck — to preview
  // those contract states.
  const paneStatus = status || "ready";

  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState(null);
  const uploading = uploadStatus === "uploading";

  const acceptUpload = (file) => {
    if (!file || !onUpload) return;
    const err = validateImageFile(file, { gifAllowed });
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    onUpload(file);
  };

  const openPicker = () => {
    if (uploading || !onUpload) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) acceptUpload(file);
    e.target.value = null; // allow re-selecting the same file (replace)
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading || !onUpload) return;
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) acceptUpload(file);
  };
  const onDragOver = (e) => {
    if (!onUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const shownUploadError = localError || (uploadStatus === "error" ? uploadError : null);

  const linkError =
    v.showHyperlink && validateField(v.hyperlink, [url()]) ? validateField(v.hyperlink, [url()]) : null;

  const patchSide = (side, next) => {
    if (v.applyMarginAll) patch({ margin: { top: next, right: next, bottom: next, left: next } });
    else patch({ margin: { ...v.margin, [side]: next } });
  };

  // "Take Photo" is appended right after Upload — same product position as the
  // Video/Audio blocks' "Record" tab (VideoSettings.jsx categories) — only when
  // the flag is ON AND this browser can actually capture (takePhotoEnabled).
  // OFF (or unsupported) => this array is byte-identical to the original
  // hardcoded 4-tab list, so the flag-OFF path is unchanged.
  const sourceTabs = [
    "Upload",
    ...(takePhotoEnabled ? ["Take Photo"] : []),
    "Embed URL",
    "Browse",
    "Recent",
  ];

  // Captured still -> the SAME validation + upload call a picked file takes
  // (acceptUpload), so a photo from the camera and a photo from disk are
  // indistinguishable to the rest of the pipeline. Then return to the Upload
  // tab so the existing uploading-spinner / error UI (already built for a
  // picked file) shows the capture's progress too — no new upload-state UI.
  const handlePhotoCapture = (file) => {
    acceptUpload(file);
    patch({ source: "Upload" });
  };

  return (
    <BlockEditorPane
      blockName="Image"
      segmentName={segmentName}
      glyph={<ImageGlyph />}
      onBack={onBack}
      status={paneStatus}
      onRetry={onRetry}
      errorMessage={errorMessage}
      emptySlot={undefined}
    >
      <EditorSection heading="Basic">
        <div>
          <TabsRow
            tabs={sourceTabs}
            active={v.source}
            onChange={(t) => patch({ source: t })}
            ariaLabel="Image source"
          />
          {renderLibrary && renderLibrary()}
          <div style={{ marginTop: 10 }}>
            {v.source === "Take Photo" && (
              <WebcamPhotoCapture
                onCapture={handlePhotoCapture}
                onCancel={() => patch({ source: "Upload" })}
              />
            )}
            {v.source === "Embed URL" && renderEmbed && renderEmbed()}
            {v.source === "Embed URL" && !renderEmbed && (
              <TextInputField
                label="Image URL"
                value={v.src}
                onChange={(s) => patch({ src: s })}
                placeholder="https://…/image.jpg"
              />
            )}
            {v.source === "Browse" &&
              (renderBrowse ? (
                renderBrowse()
              ) : (
                <DropZone as="div" aria-hidden="true">
                  Search the media library from the full builder.
                </DropZone>
              ))}
            {v.source === "Recent" &&
              (renderRecent ? (
                renderRecent()
              ) : (
                <DropZone as="div" aria-hidden="true">
                  Your recent uploads appear here in the full builder.
                </DropZone>
              ))}
            {v.source === "Upload" && (
              <>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  onChange={onFileInputChange}
                  data-testid="image-file-input"
                />
                <DropZone
                  role="button"
                  tabIndex={0}
                  aria-label="Upload image: drag and drop, or choose a file"
                  aria-busy={uploading}
                  $active={dragActive}
                  onClick={openPicker}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPicker();
                    }
                  }}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragEnter={onDragOver}
                  onDragLeave={onDragLeave}
                  data-testid="image-dropzone"
                >
                  {uploading ? (
                    <DZProgress>
                      <CircularProgress size={22} />
                      Uploading…
                    </DZProgress>
                  ) : (
                    <>
                      Drag and drop image here, or{" "}
                      <DZLink type="button" onClick={(e) => { e.stopPropagation(); openPicker(); }}>
                        Choose Image
                      </DZLink>
                    </>
                  )}
                </DropZone>
                {shownUploadError && (
                  <UploadError role="alert" data-testid="image-upload-error">
                    {shownUploadError}
                  </UploadError>
                )}
              </>
            )}
          </div>
        </div>

        {v.src && (
          <UpCard>
            <Thumb $src={v.src} aria-hidden="true" />
            <UpMeta>
              <UpName>{v.title || "Uploaded image"}</UpName>
            </UpMeta>
            <UpActions>
              <IconBtn type="button" onClick={onCopyUrl} aria-label="Copy image URL">Copy URL</IconBtn>
              <IconBtn type="button" $danger onClick={onRemove} aria-label="Delete image">Delete</IconBtn>
            </UpActions>
          </UpCard>
        )}

        {/* QA-54 (Kelly, 2026-07-31): one word for text-on-an-image, everywhere —
            "Caption". This single field is dual-purpose: it is the visible caption
            AND the img alt attribute, so the placeholder says so rather than hiding
            the accessibility duty behind a rename. */}
        <TextInputField
          label="Caption"
          value={v.title}
          onChange={(s) => patch({ title: s })}
          placeholder="Describe the image — also used as alt text"
        />
        <Toggle
          label="Show caption"
          checked={v.showImageTitle}
          onChange={(b) => patch({ showImageTitle: b })}
        />
        <SegChoice
          label="Alignment"
          value={v.alignment}
          options={[
            { label: "Left", value: "left" },
            { label: "Right", value: "right" },
          ]}
          onChange={(a) => patch({ alignment: a })}
        />
      </EditorSection>

      {/* IMAGE_CAPTION_OVERLAY (DL-037 / N40, flag-gated). OFF renders nothing here,
          keeping the editor byte-identical; the shared Text overlay control is the
          same one that styles Container title bars (N41). */}
      {captionOverlayEnabled && (
        <EditorSection heading="Text overlay">
          <CaptionOverlayControl
            value={v.captionOverlay}
            onChange={(next) => patch({ captionOverlay: next })}
          />
        </EditorSection>
      )}

      <AdvancedOptions>
        <Toggle
          label="Add Hyperlink to Image"
          checked={v.showHyperlink}
          onChange={(b) => patch({ showHyperlink: b })}
        />
        {v.showHyperlink && (
          <TextInputField
            label="Link URL"
            value={v.hyperlink}
            onChange={(s) => patch({ hyperlink: s })}
            error={linkError}
            placeholder="https://…"
          />
        )}
        <LabeledSlider
          label="Resizing"
          value={v.width}
          min={10}
          max={100}
          unit="%"
          onChange={(n) => patch({ width: n })}
        />
        <LabeledSlider
          label="Corner Rounding"
          value={v.borderRadius}
          min={0}
          max={50}
          unit="px"
          onChange={(n) => patch({ borderRadius: n })}
        />
        <ColorField
          label="Border"
          color={v.borderColor}
          onChange={(c) => patch({ borderColor: c })}
          onReset={() => patch({ borderColor: DEFAULT.borderColor })}
          slider={{ title: "Border Width", value: v.borderWidth, min: 0, max: 8, unit: "px" }}
          onSlider={(n) => patch({ borderWidth: n })}
        />
        <ColorField
          label="Drop Shadow"
          color={v.dropShadowColor || "transparent"}
          onChange={(c) => patch({ dropShadowColor: c })}
          onReset={() => patch({ dropShadowColor: "" })}
        />
        <QuadStepper
          label="Margin"
          value={v.margin}
          onChange={patchSide}
          applyAll={v.applyMarginAll}
          onApplyAll={(b) => patch({ applyMarginAll: b })}
        />
      </AdvancedOptions>
    </BlockEditorPane>
  );
};

export default ImageEditor;
